import { InternalPlanPurchaseProviderContextPropsContext } from '../context/InternalPlanPurchaseProviderContextProps'
import { PlanPurchaseProviderContextHook } from '@app/plans/src/purchase/PlanPurchaseProvider/context-hooks/PlanPurchaseProviderContextHook'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { getPlanTypeDirection } from '@app/common/src/utils/getPlanTypeDirection'
import { isPilotPlanType } from '@app/common/src/utils/isPilotPlanType'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useEffect } from 'react'
import { useIsBillingAdmin } from '@dtx-company/inter-app/src/hooks/useIsBillingAdmin/useIsBillingAdmin'
import { usePlanPurchaseExperienceSubscriptionInfo } from '@app/common/src/components/PlanPurchaseExperience/hooks/usePlanPurchaseExperienceSubscriptionInfo'

export const usePlanPurchaseProviderLoadingState: PlanPurchaseProviderContextHook = (
  props,
  context,
  updateContext
): void => {
  const { onLoadError } = props

  const authState = useAuthState()
  const isBillingAdmin = useIsBillingAdmin()
  const { currentSubscriptionInfoData, isLoading: currentSubscriptionInfoDataIsLoading } =
    usePlanPurchaseExperienceSubscriptionInfo()

  const { isLoaded, selectedPlan } = context

  // Set error or loaded
  useEffect(() => {
    // SWR hooks can re-fire when the JWT changes after purchase, which can lead to
    // issues like firing `already-on-plan` for the plan you just purchased
    // so ensure we don't redo anything once initially loaded
    if (isLoaded) return

    if (
      !authState.isAuthChecked ||
      isBillingAdmin === undefined ||
      currentSubscriptionInfoDataIsLoading ||
      !selectedPlan
    ) {
      updateContext({ isLoaded: false, loadingErrorType: undefined })
      return
    }

    function setError(
      loadingErrorType: InternalPlanPurchaseProviderContextPropsContext['loadingErrorType']
    ): void {
      updateContext({ isLoaded: false, loadingErrorType })
      onLoadError?.(loadingErrorType)
    }

    // NOTE: we want to use the current subscription plan type here, not the
    // billingPlanType, so that someone that has PRO-for-free can still choose
    // to buy a PRO subscription.
    const currentPlanType = currentSubscriptionInfoData?.plan.type || PlanType.FREE
    const planTypeDirection = getPlanTypeDirection(currentPlanType, selectedPlan.type)
    const isPilot = isPilotPlanType(currentPlanType)

    if (!authState.isAuthenticated) {
      setError('not-logged-in')
    } else if (!isBillingAdmin) {
      setError('not-billing-admin')
    } else if (!isPilot && planTypeDirection < 0) {
      setError('downgrade-not-allowed')
    } else if (planTypeDirection === 0) {
      setError('already-on-plan')
    } else {
      updateContext({ loadingErrorType: undefined, isLoaded: true })
    }
  }, [
    authState.isAuthChecked,
    authState.isAuthenticated,
    currentSubscriptionInfoData?.plan.type,
    currentSubscriptionInfoDataIsLoading,
    isBillingAdmin,
    isLoaded,
    onLoadError,
    selectedPlan,
    updateContext
  ])
}
